import { debounce } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { NumberField } from 'sutra/components/NumberField/NumberField'

import {
  CardActionButton,
  CardRoot,
  FilterChip,
  GroupPrefix,
  RevealOnCardHover,
} from 'components/filter-card/filter-card.styles'
import { NumericOperatorPopover } from 'features/charts-v2/components/NumericOperatorPopover'
import {
  type NumericFilterFieldType,
  type NumericOperatorType,
} from 'features/charts-v2/types'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'

const NUMERIC_OPERATOR_OPTIONS: {
  value: NumericOperatorType
  label: string
}[] = [
  { value: 'greater than', label: 'Greater than' },
  { value: 'greater than or equal to', label: 'Greater than or equal to' },
  { value: 'less than', label: 'Less than' },
  { value: 'less than or equal to', label: 'Less than or equal to' },
  { value: 'equal to', label: 'Equal to' },
  { value: 'not equal to', label: 'Not equal to' },
]

const getOperatorLabel = (operator: NumericOperatorType): string => {
  return (
    NUMERIC_OPERATOR_OPTIONS.find((opt) => opt.value === operator)?.label ||
    operator
  )
}

interface NumericFilterCardProps {
  field: NumericFilterFieldType
  label: string
  operator: NumericOperatorType
  value: number | undefined
  onOperatorChange: (operator: NumericOperatorType) => void
  onValueChange: (value: number | undefined) => void
  onDelete: () => void
  // The picker group this card sits under ("Threshold"), shown as a muted
  // `Group > ` before the title. Omitted when hierarchy is off.
  groupLabel?: string
}

export const NumericFilterCard: React.FC<NumericFilterCardProps> = ({
  label,
  groupLabel,
  operator,
  value,
  onOperatorChange,
  onValueChange,
  onDelete,
}) => {
  const [isOperatorDropdownOpen, setIsOperatorDropdownOpen] = useState(false)
  const operatorRef = useRef<HTMLSpanElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  // Local state for immediate UI feedback
  const [inputValue, setInputValue] = useState<number | undefined>(value)

  // Sync with external value changes (e.g., undo/redo, reset)
  useEffect(() => {
    setInputValue(value)
  }, [value])

  // Store the latest onValueChange callback in a ref to avoid stale closures
  const onValueChangeRef = useRef(onValueChange)
  useEffect(() => {
    onValueChangeRef.current = onValueChange
  }, [onValueChange])

  // Debounce value changes to avoid triggering API requests on every keystroke
  const debouncedOnValueChange = useRef(
    debounce((newValue: number | undefined) => {
      onValueChangeRef.current(newValue)
    }, 500),
  ).current

  // Cancel pending debounced calls on unmount
  useEffect(() => {
    return () => {
      debouncedOnValueChange.cancel()
    }
  }, [debouncedOnValueChange])

  const handleValueChange = useCallback(
    (newValue: number | null) => {
      // Update local state immediately for responsive UI
      setInputValue(newValue ?? undefined)
      // Debounce the parent update to avoid excessive API calls
      debouncedOnValueChange(newValue ?? undefined)
    },
    [debouncedOnValueChange],
  )

  return (
    <>
      <CardRoot ref={cardRef} $disabled={false}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
          <div style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span
                  style={{
                    fontWeight: '500',
                    color: '#374151',
                    fontSize: '14px',
                  }}
                >
                  {groupLabel && (
                    <GroupPrefix>
                      {groupLabel} {'> '}
                    </GroupPrefix>
                  )}
                  {label}
                </span>
              </div>

              {/* Delete Button - only visible on card hover, via CSS */}
              <RevealOnCardHover>
                <CardActionButton
                  type="button"
                  $variant="danger"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete()
                  }}
                  title="Remove filter"
                >
                  ×
                </CardActionButton>
              </RevealOnCardHover>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px',
                gap: '8px',
              }}
            >
              <FilterChip
                ref={operatorRef}
                $open={isOperatorDropdownOpen}
                $color="#6b7280"
                onClick={() =>
                  setIsOperatorDropdownOpen(!isOperatorDropdownOpen)
                }
              >
                {getOperatorLabel(operator)}
              </FilterChip>
              <NumberField
                value={inputValue ?? null}
                onValueChange={handleValueChange}
                style={{ width: '90px', fontSize: '12px', height: '30px' }}
                min={0}
                placeholder={ALIAS_FOR_NULL_VALUES}
              />
            </div>
          </div>
        </div>
      </CardRoot>

      {isOperatorDropdownOpen && (
        <NumericOperatorPopover
          onOperatorSelect={onOperatorChange}
          onClose={() => setIsOperatorDropdownOpen(false)}
          selectedOperator={operator}
          triggerRef={operatorRef}
          cardRef={cardRef}
        />
      )}
    </>
  )
}
